import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, LanguageContextType } from '../types';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('preferred-language');
    if (saved && (saved === 'en' || saved === 'de')) {
      return saved as Language;
    }
    // Default to German for German users, English otherwise
    return navigator.language.startsWith('de') ? 'de' : 'en';
  });

  useEffect(() => {
    localStorage.setItem('preferred-language', language);
  }, [language]);

  const t = (key: string): string => {
    return (translations[language] as any)[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Navigation
    'nav.about': 'About Me',
    'nav.skills': 'Skills',
    'nav.blog': 'Blog',
    'nav.logout': 'Logout',
    
    // Auth
    'auth.title': 'Access Portfolio',
    'auth.description': 'Please enter credentials to access the developer portfolio',
    'auth.username': 'Username:',
    'auth.password': 'Password:',
    'auth.login': 'Login',
    'auth.error': 'Invalid credentials. Please try again.',
    
    // Portfolio Header
    'portfolio.name': 'Tobias Stumpf',
    'portfolio.title': 'Software Developer & IoT Specialist',
    'portfolio.footer': '© 2025 Tobias Stumpf. All rights reserved.',
    'portfolio.social.linkedin': 'LinkedIn',
    'portfolio.social.github': 'GitHub',
    'portfolio.social.email': 'Email',
    
    // Developer Profile
    'profile.hello': 'Hello! I\'m Tobias',
    'profile.intro': 'An experienced Software Developer and IoT Specialist with over 10 years in full-stack development, specializing in Python, Java, and modern web technologies.',
    'profile.about.title': 'About Me',
    'profile.about.intro': 'Experienced software developer with 10+ years in full-stack development and IoT solutions. I specialize in Python, Java, JavaScript, and modern web technologies, with extensive experience in building scalable applications and IoT systems.',
    'profile.about.focus': 'Based in Mannheim, Germany, I focus on creating robust software solutions and have worked across various industries from technology consulting to energy management systems.',
    'profile.experience.title': 'Experience',
    'profile.experience.iot.title': 'Software-Entwickler IoT',
    'profile.experience.iot.period': 'Jan 2022 - Mar 2024',
    'profile.experience.iot.company': 'beegy GmbH',
    'profile.experience.iot.task1': 'Developed IoT software solutions using Python and Java',
    'profile.experience.iot.task2': 'Built scalable applications for energy management systems',
    'profile.experience.iot.task3': 'Collaborated on software architecture and system design',
    'profile.experience.fullstack.title': 'Full-stack Developer',
    'profile.experience.fullstack.period': 'Apr 2014 - Dec 2021',
    'profile.experience.fullstack.company': 'Cytel Technology AG',
    'profile.experience.fullstack.task1': 'Developed web applications using JavaScript, HTML5, and PHP',
    'profile.experience.fullstack.task2': 'Built responsive frontend interfaces and backend systems',
    'profile.experience.fullstack.task3': 'Implemented comprehensive testing with PHPUnit',
    'profile.experience.fullstack.task4': 'Worked in hybrid environment with modern development practices',
    'profile.experience.current': 'Senior Full-Stack Developer',
    'profile.experience.current.company': 'Tech Solutions GmbH',
    'profile.experience.current.period': '2022 - Present',
    'profile.experience.current.desc': 'Leading development of enterprise web applications using React, Node.js, and cloud technologies. Implemented CI/CD pipelines that reduced deployment time by 60%.',
    'profile.experience.previous': 'Full-Stack Developer',
    'profile.experience.previous.company': 'Digital Innovations AG',
    'profile.experience.previous.period': '2020 - 2022',
    'profile.experience.previous.desc': 'Developed responsive web applications and RESTful APIs. Collaborated with cross-functional teams to deliver projects on time and within budget.',
    'profile.education.title': 'Education',
    'profile.education.degree': 'Bachelor of Science in Computer Science',
    'profile.education.university': 'Technical University of Munich',
    'profile.education.period': '2016 - 2020',
    'profile.contact.title': 'Get In Touch',
    'profile.contact.description': 'I\'m always interested in new opportunities and interesting projects. Feel free to reach out!',
    
    // Projects
    'profile.projects.title': 'Featured Projects',
    'profile.projects.iot.title': 'Smart Energy Management System',
    'profile.projects.iot.description': 'Developed a comprehensive IoT solution for residential solar installations with real-time optimization and grid integration.',
    'profile.projects.webapp.title': 'Real-time Analytics Dashboard',
    'profile.projects.webapp.description': 'Built a scalable web application for data visualization and business intelligence with live updates and interactive charts.',
    'profile.projects.automation.title': 'DevOps Automation Pipeline',
    'profile.projects.automation.description': 'Created a complete CI/CD pipeline that reduced deployment time by 60% and improved code quality through automated testing.',
    'profile.projects.status.completed': 'Completed',
    'profile.projects.status.ongoing': 'In Progress',
    'profile.projects.private': 'Private Repository',
    'profile.projects.demo': 'View Demo',
    
    // Skills
    'skills.title': 'Technical Skills',
    'skills.description': 'Technologies and tools I work with',
    'skills.experience': 'Years Experience',
    'skills.technologies': 'Technologies',
    'skills.projects': 'Projects',
    'skills.passion': 'Passion',
    'skills.frontend': 'Frontend Development',
    'skills.backend': 'Backend Development',
    'skills.iot': 'IoT & Embedded Systems',
    'skills.tools': 'Tools & Methodologies',
    'skills.certifications': 'Certifications & Learning',
    'skills.cert.safe': 'SAFe 6 Practitioner',
    'skills.cert.safe.status': 'Certified',
    
    // Blog
    'blog.title': 'Developer Blog',
    'blog.description': 'Insights on development, DevOps, and technology trends',
    'blog.back': 'Back to Blog',
    'blog.all': 'All',
    'blog.readMore': 'Read More',
    'blog.readTime': 'min read',
    'blog.loading': 'Loading blog posts...',
    'blog.noPosts': 'No blog posts available.',
    'blog.vgwort': 'Article monetized via VG Wort',
    
    // Language Switcher
    'language.english': 'English',
    'language.german': 'Deutsch',
    
    // Hamburger Menu
    'menu.settings': 'Settings',
    'menu.language': 'Language',
  },
  de: {
    // Navigation
    'nav.about': 'Über mich',
    'nav.skills': 'Fähigkeiten',
    'nav.blog': 'Blog',
    'nav.logout': 'Abmelden',
    
    // Auth
    'auth.title': 'Portfolio-Zugang',
    'auth.description': 'Bitte geben Sie die Zugangsdaten ein, um das Entwickler-Portfolio zu betreten',
    'auth.username': 'Benutzername:',
    'auth.password': 'Passwort:',
    'auth.login': 'Anmelden',
    'auth.error': 'Ungültige Zugangsdaten. Bitte versuchen Sie es erneut.',
    
    // Portfolio Header
    'portfolio.name': 'Tobias Stumpf',
    'portfolio.title': 'Software-Entwickler & IoT-Spezialist',
    'portfolio.footer': '© 2025 Tobias Stumpf. Alle Rechte vorbehalten.',
    'portfolio.social.linkedin': 'LinkedIn',
    'portfolio.social.github': 'GitHub',
    'portfolio.social.email': 'E-Mail',
    
    // Developer Profile
    'profile.hello': 'Hallo! Ich bin Tobias',
    'profile.intro': 'Ein erfahrener Software-Entwickler und IoT-Spezialist mit über 10 Jahren Erfahrung in der Full-Stack-Entwicklung, spezialisiert auf Python, Java und moderne Web-Technologien.',
    'profile.about.title': 'Über mich',
    'profile.about.intro': 'Erfahrener Software-Entwickler mit über 10 Jahren Erfahrung in der Full-Stack-Entwicklung und IoT-Lösungen. Ich spezialisiere mich auf Python, Java, JavaScript und moderne Web-Technologien mit umfangreicher Erfahrung im Aufbau skalierbarer Anwendungen und IoT-Systeme.',
    'profile.about.focus': 'Mit Sitz in Mannheim, Deutschland, konzentriere ich mich auf die Entwicklung robuster Software-Lösungen und habe in verschiedenen Branchen von Technologieberatung bis hin zu Energiemanagementsystemen gearbeitet.',
    'profile.experience.title': 'Berufserfahrung',
    'profile.experience.iot.title': 'Software-Entwickler IoT',
    'profile.experience.iot.period': 'Jan 2022 - Mär 2024',
    'profile.experience.iot.company': 'beegy GmbH',
    'profile.experience.iot.task1': 'Entwicklung von IoT-Software-Lösungen mit Python und Java',
    'profile.experience.iot.task2': 'Aufbau skalierbarer Anwendungen für Energiemanagementsysteme',
    'profile.experience.iot.task3': 'Zusammenarbeit bei Software-Architektur und Systemdesign',
    'profile.experience.fullstack.title': 'Full-Stack-Entwickler',
    'profile.experience.fullstack.period': 'Apr 2014 - Dez 2021',
    'profile.experience.fullstack.company': 'Cytel Technology AG',
    'profile.experience.fullstack.task1': 'Entwicklung von Webanwendungen mit JavaScript, HTML5 und PHP',
    'profile.experience.fullstack.task2': 'Aufbau responsiver Frontend-Schnittstellen und Backend-Systeme',
    'profile.experience.fullstack.task3': 'Implementierung umfassender Tests mit PHPUnit',
    'profile.experience.fullstack.task4': 'Arbeit in hybrider Umgebung mit modernen Entwicklungspraktiken',
    'profile.experience.current': 'Senior Full-Stack-Entwickler',
    'profile.experience.current.company': 'Tech Solutions GmbH',
    'profile.experience.current.period': '2022 - Heute',
    'profile.experience.current.desc': 'Leitung der Entwicklung von Unternehmens-Webanwendungen mit React, Node.js und Cloud-Technologien. Implementierung von CI/CD-Pipelines, die die Bereitstellungszeit um 60% reduzierten.',
    'profile.experience.previous': 'Full-Stack-Entwickler',
    'profile.experience.previous.company': 'Digital Innovations AG',
    'profile.experience.previous.period': '2020 - 2022',
    'profile.experience.previous.desc': 'Entwicklung responsiver Webanwendungen und RESTful-APIs. Zusammenarbeit mit funktionsübergreifenden Teams zur termingerechten und budgetkonformen Projektlieferung.',
    'profile.education.title': 'Ausbildung',
    'profile.education.degree': 'Bachelor of Science in Informatik',
    'profile.education.university': 'Technische Universität München',
    'profile.education.period': '2016 - 2020',
    'profile.contact.title': 'Kontakt aufnehmen',
    'profile.contact.description': 'Ich bin immer interessiert an neuen Möglichkeiten und interessanten Projekten. Zögern Sie nicht, mich zu kontaktieren!',
    
    // Projects
    'profile.projects.title': 'Ausgewählte Projekte',
    'profile.projects.iot.title': 'Smart Energy Management System',
    'profile.projects.iot.description': 'Entwicklung einer umfassenden IoT-Lösung für private Solaranlagen mit Echtzeit-Optimierung und Netzintegration.',
    'profile.projects.webapp.title': 'Echtzeit-Analytics Dashboard',
    'profile.projects.webapp.description': 'Aufbau einer skalierbaren Webanwendung für Datenvisualisierung und Business Intelligence mit Live-Updates und interaktiven Charts.',
    'profile.projects.automation.title': 'DevOps Automatisierungs-Pipeline',
    'profile.projects.automation.description': 'Erstellung einer vollständigen CI/CD-Pipeline, die die Bereitstellungszeit um 60% reduzierte und die Codequalität durch automatisierte Tests verbesserte.',
    'profile.projects.status.completed': 'Abgeschlossen',
    'profile.projects.status.ongoing': 'In Bearbeitung',
    'profile.projects.private': 'Privates Repository',
    'profile.projects.demo': 'Demo ansehen',
    
    // Skills
    'skills.title': 'Technische Fähigkeiten',
    'skills.description': 'Technologien und Tools, mit denen ich arbeite',
    'skills.experience': 'Jahre Erfahrung',
    'skills.technologies': 'Technologien',
    'skills.projects': 'Projekte',
    'skills.passion': 'Leidenschaft',
    'skills.frontend': 'Frontend-Entwicklung',
    'skills.backend': 'Backend-Entwicklung',
    'skills.iot': 'IoT & Embedded Systems',
    'skills.tools': 'Tools & Methodologien',
    'skills.certifications': 'Zertifizierungen & Lernen',
    'skills.cert.safe': 'SAFe 6 Practitioner',
    'skills.cert.safe.status': 'Zertifiziert',
    
    // Blog
    'blog.title': 'Entwickler-Blog',
    'blog.description': 'Einblicke in Entwicklung, DevOps und Technologie-Trends',
    'blog.back': 'Zurück zum Blog',
    'blog.all': 'Alle',
    'blog.readMore': 'Weiterlesen',
    'blog.readTime': 'Min. Lesezeit',
    'blog.loading': 'Blog-Beiträge werden geladen...',
    'blog.noPosts': 'Keine Blog-Beiträge verfügbar.',
    'blog.vgwort': 'Artikel monetarisiert über VG Wort',
    
    // Language Switcher
    'language.english': 'English',
    'language.german': 'Deutsch',
    
    // Hamburger Menu
    'menu.settings': 'Einstellungen',
    'menu.language': 'Sprache',
  }
};